<template>
    <h4 class="mb-3">I'm looking for financial advice</h4>

    <p v-for="(text, i) in templateTextArray('dboptions.next-steps.ifa.introduction.body', { ifa })" :key="i">{{ text }}</p>

    <div v-if="showContent">
        <div class="col-12 col-md-8 col-lg-6">
            <text-input ref="nameInput"
                        v-model="nameModel"
                        description="Name"
                        class="mt-4">
            </text-input>

            <text-input ref="phoneInput"
                        v-model="phoneNumberModel"
                        description="Phone number"
                        :regex="Constants.RegularExpressions.PhoneNumber"
                        :key-press-regex="Constants.RegularExpressions.PhoneNumberChars"
                        maxlength="20"
                        class="mt-4">
            </text-input>

            <text-input ref="emailInput"
                        v-model="emailModel"
                        description="Email"
                        :regex="Constants.RegularExpressions.Email"
                        :key-press-regex="Constants.RegularExpressions.EmailChars"
                        class="mt-4">
            </text-input>
        </div>

        <div class="mt-4 form-check form-switch me-2">
            <input id="consentToPrivacyNotice" type="checkbox" class="form-check-input" v-model="privacyConsent" role="switch" @change="handleConsentToggle"
                   :disabled="consentToggleDisabled" />
            <label class="form-check-label ms-3 fw-bold" for="consentToPrivacyNotice">
                I confirm I have read Aon's <a href="https://www.aon.com/unitedkingdom/retirement-investment/retirement-investment-services-privacy-statement.jsp" target="_blank">Privacy Notice</a> and agree to Aon sharing my information with the IFA for the purposes
                of contacting me and providing advice.
            </label>
        </div>

        <div class="mt-4">
            <p class="fst-italic">
                By submitting this form you are confirming that you have read Aon's Privacy Notice and agree to your data being shared with the IFA. The IFA will
                contact you directly. Aon processes your personal data in line with our privacy obligation under the Data Protection Act. For more details please 
                see the privacy notice
                <a href="https://www.aon.com/unitedkingdom/retirement-investment/retirement-investment-services-privacy-statement.jsp" target="_blank">here</a>.
            </p>
        </div>

        <dynamic-advice-viewer :message="submissionError" alertContext="danger"></dynamic-advice-viewer>

        <div class="mt-4">
            <button class="btn btn-secondary btn-book" @click="onBookSessionClicked" :disabled="disableSubmit">Submit</button>
        </div>
    </div>

    <dynamic-advice-viewer :message="submissionSuccessMessage" alertContext="success"></dynamic-advice-viewer>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useStore } from "vuex";
import { app } from "@/guiide/ts/app";
import TextInput from "@/guiide/components/common/TextInput.vue";
import Constants from "@/guiide/ts/constants";
import DynamicAdviceViewer from "@/guiide/components/common/DynamicAdviceViewer.vue";
import ConsentType from "@/guiide/ts/consent/consentType";
import ExternalAgency from "@/guiide/ts/consent/externalAgency";
import PermittedEntityType from "@/guiide/ts/consent/permittedEntityType";
import { hasConsented } from "@/guiide/ts/utilities";
import Result from "@/guiide/ts/result";
import { useUIContent } from '@/guiide/ts/configuration/useUIContent';

const store = useStore();
const { templateTextArray } = useUIContent();

const nameInput = ref(null);
const nameModel = ref("");
const phoneInput = ref(null);
const phoneNumberModel = ref("");
const emailInput = ref(null);
const emailModel = ref("");

const consentToggleDisabled = computed<boolean>(() => store.state.transactionOngoing);

const hasConsentedToPrivacyNotice = computed<boolean>(() => hasConsented(ExternalAgency.Aon, ConsentType.PrivacyNoticeAccepted));
const privacyConsent = ref(hasConsentedToPrivacyNotice.value);

const disableSubmit = ref<boolean>(!hasConsentedToPrivacyNotice.value);

const submissionError = ref("");
const submissionSuccessMessage = ref("");

const showContent = ref(true);

const ifa = computed(() => store.state.retiree.finalSalaries.schemes[0].linkedIfas[0].name);

async function handleConsentToggle() {
    disableSubmit.value = true;

    let consented = privacyConsent.value;

    let toggleConsentResult: Result;
    if (consented) {
        toggleConsentResult = await app.grantConsent(PermittedEntityType.ExternalAgency, ExternalAgency.Aon, ConsentType.PrivacyNoticeAccepted, true);
    } else {
        toggleConsentResult = await app.revokeConsent(PermittedEntityType.ExternalAgency, ExternalAgency.Aon, ConsentType.PrivacyNoticeAccepted);
    }

    if (toggleConsentResult.successful) {
        disableSubmit.value = !consented;
    } else {
        submissionError.value = "Failed to save change.";
    }
}

async function onBookSessionClicked() {
    submissionError.value = "";

    if (!nameInput.value.isValid()) {
        submissionError.value = "Invalid name.";
        return;
    }
    if (!phoneInput.value.isValid()) {
        submissionError.value = "Invalid phone number.";
        return;
    }
    if (!emailInput.value.isValid()) {
        submissionError.value = "Invalid email address.";
        return;
    }

    if (!hasConsentedToPrivacyNotice.value) {
        submissionError.value = "Please confirm you agree to Aon sharing your information.";
        return;
    }

    var ifaId = store.state.retiree.finalSalaries.schemes[0].linkedIfas[0].id;
    const result = await app.requestFinancialAdvice(nameModel.value, emailModel.value, phoneNumberModel.value, ifaId);

    if (!result.successful) {
        submissionError.value = result.message;
    } else {
        showContent.value = false;
        submissionSuccessMessage.value = "Request sent.";
    }
}
</script>